import { oc } from '@orpc/contract';
import { z } from 'zod';
import { catalogErrors, FieldValues, ImageRole } from './catalog.js';
import { Id } from './common.js';
import { Code } from './meta.js';
/** Куда импортировать тип Numista (docs/PLAN.md, раздел 2, п. 18). */
export const NumistaTarget = z.enum(['catalog', 'private']);
export const NumistaId = z.coerce.number().int().positive();
export const NumistaStatus = z.object({
    /** Задан ли ключ API на сервере. */
    configured: z.boolean(),
    /** Месяц учёта квоты, YYYY-MM. */
    month: z.string(),
    used: z.number().int(),
    quota: z.number().int(),
});
/** Уже импортированные записи с этим N#: общая и своя приватная. */
export const NumistaExisting = z.object({ catalog: Id.nullable(), private: Id.nullable() });
/** Год или диапазон «1800-1850»; число или диапазон «9-11» (как их понимает Numista API). */
const Range = z
    .string()
    .trim()
    .regex(/^\d{1,4}(?:[.,]\d+)?(?:-\d{1,4}(?:[.,]\d+)?)?$/, 'range')
    .transform((v) => v.replace(/,/g, '.'));
export const NumistaCategory = z.enum(['coin', 'banknote', 'exonumia']);
export const NumistaOrder = z.enum(['relevance', 'value', 'date', 'reference']);
/**
 * Поиск в каталоге Numista (GET /types). Нужен хотя бы один из q, issuer, catalogue, date,
 * year — так требует API.
 */
export const NumistaSearchInput = z
    .object({
    q: z.string().trim().min(2).max(200).optional(),
    page: z.coerce.number().int().min(1).max(100).optional(),
    /** Код эмитента Numista («russia», «thailande»). */
    issuer: z.string().trim().max(100).optional(),
    category: NumistaCategory.optional(),
    /** id каталога Numista (KM, Pick…) и номер в нём. */
    catalogue: z.coerce.number().int().positive().optional(),
    number: z.string().trim().max(64).optional(),
    /** Год выпуска по григорианскому календарю или диапазон. */
    date: Range.optional(),
    /** Год, как написан на предмете, или диапазон. */
    year: Range.optional(),
    /** Диаметр (наибольший размер), мм, или диапазон. */
    size: Range.optional(),
    /** Вес, г, или диапазон. */
    weight: Range.optional(),
    order: NumistaOrder.optional(),
})
    .refine((x) => x.q || x.issuer || x.catalogue || x.date || x.year, {
    message: 'query_required',
    path: ['q'],
});
export const NumistaIssuerOption = z.object({
    code: z.string(),
    name: z.string(),
    /** Раздел, к которому относится эмитент («Germany» для «German Empire»). */
    parent: z.string().nullable(),
});
export const NumistaCatalogueOption = z.object({
    id: z.number().int(),
    code: z.string(),
    title: z.string(),
});
export const NumistaSearchItem = z.object({
    numistaId: z.number().int(),
    title: z.string(),
    issuer: z.string().nullable(),
    minYear: z.number().int().nullable(),
    maxYear: z.number().int().nullable(),
    category: z.string().nullable(),
    thumbnail: z.string().nullable(),
    existing: NumistaExisting,
});
export const NumistaSearchResult = z.object({
    count: z.number().int(),
    page: z.number().int(),
    items: z.array(NumistaSearchItem),
});
/**
 * Значение Numista, которое не удалось сопоставить со справочниками или enum. create — запись
 * справочника будет добавлена при импорте (только для редакторов и админов).
 */
export const NumistaUnmatched = z.object({
    field: z.string(),
    value: z.string(),
    create: z.boolean().optional(),
});
export const NumistaPreview = z.object({
    numistaId: z.number().int(),
    url: z.string().nullable(),
    title: z.string(),
    /** Предлагаемый тип объекта; при импорте можно выбрать другой. */
    typeCode: Code,
    /** Значения полей уровня каталога в формате формы (ссылки — id справочников). */
    values: FieldValues,
    variants: z.array(z.object({ numistaIssueId: z.number().int(), label: z.string(), values: FieldValues })),
    images: z.array(z.object({
        role: ImageRole,
        /** Сторона в Numista: у водяного знака роль «другое», но подпись своя. */
        side: z.enum(['obverse', 'reverse', 'edge', 'watermark']),
        thumbnail: z.string().nullable(),
    })),
    unmatched: z.array(NumistaUnmatched),
    existing: NumistaExisting,
    /** Оригиналы переведённых полей (values — уже на русском). */
    originals: z.record(z.string(), z.string()),
    /** done — всё переведено; partial — часть текстов осталась на языке Numista (сбой или квота). */
    translation: z.enum(['done', 'partial']),
});
export const NumistaImportInput = z.object({
    numistaId: NumistaId,
    target: NumistaTarget,
    typeCode: Code.optional(),
    /** Эмитент, если Numista-эмитент не сопоставился автоматически (запоминается). */
    issuerId: Id.optional(),
    /** Перевести тексты (название, описания) на русский; оригиналы сохраняются. По умолчанию да. */
    translate: z.boolean().default(true),
});
export const NumistaImportResult = z.object({ id: Id, created: z.boolean() });
export const numistaErrors = {
    NUMISTA_NOT_CONFIGURED: { status: 503, message: 'numista_not_configured' },
    NUMISTA_QUOTA: { status: 429, message: 'numista_quota' },
    NUMISTA_UNAVAILABLE: { status: 502, message: 'numista_unavailable' },
};
export const numistaContract = {
    status: oc.route({ method: 'GET', path: '/integrations/numista/status' }).output(NumistaStatus),
    search: oc
        .route({ method: 'GET', path: '/integrations/numista/search' })
        .errors(numistaErrors)
        .input(NumistaSearchInput)
        .output(NumistaSearchResult),
    /** Справочники для фильтров поиска (один запрос к Numista в месяц, дальше — кэш). */
    issuers: oc
        .route({ method: 'GET', path: '/integrations/numista/issuers' })
        .errors(numistaErrors)
        .output(z.array(NumistaIssuerOption)),
    catalogues: oc
        .route({ method: 'GET', path: '/integrations/numista/catalogues' })
        .errors(numistaErrors)
        .output(z.array(NumistaCatalogueOption)),
    preview: oc
        .route({ method: 'GET', path: '/integrations/numista/types/{numistaId}' })
        .errors({ ...numistaErrors, NOT_FOUND: catalogErrors.NOT_FOUND })
        .input(z.object({ numistaId: NumistaId }))
        .output(NumistaPreview),
    import: oc
        .route({ method: 'POST', path: '/integrations/numista/import' })
        .errors({ ...numistaErrors, ...catalogErrors })
        .input(NumistaImportInput)
        .output(NumistaImportResult),
};
//# sourceMappingURL=numista.js.map